import React, { useState, useEffect } from "react";
import useOnlineStatus from "../hooks/useOnlineStatus";
import useOfflineSync from "../hooks/useOfflineSync";

const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';
const ATTACH_BASE_URL = API_BASE_URL.replace(/\/api$/, '') + '/self_registered_attachments';

export default function MemberForm({ onMemberAdded, onClose, editingMember, currentUserEmail }) {
  const initialState = {
    firstName: "",
    middleName: "",
    lastName: "",
    gender: "",
    labourID: "",
    labourIDNumber: "",
    workPlace: "",
    officeName: "",
    workType: "",
    jobResponsibility: "",
    dateOfBirth: "",
    age: "",
    phoneNumber: "",
    AltPhoneNumber: "",
    email: "",
    location: "Addis Ababa",
    subCity: "",
    houseNumber: "",
    woreda: "",
    specificLocation: "",
    disabilityType: "",
    disabilityOther: "",
    educationLevel: "",
    disabilityDescription: "",
    maritalStatus: "",
    numberOfFamilies: "",
    employmentStatus: "",
    supportNeeded: "",
    workNeeded: "",
    supportReceived: "",
    idType: "",
    idNumber: "",
    idFile: null,
    photoFile: null,
    opdName: "",
  };

  const isImageFile = (filename) => {
    if (!filename) return false;
    const ext = String(filename).split('.').pop().toLowerCase();
    return ['jpg','jpeg','png','gif','bmp','webp','svg'].includes(ext);
  };

  const subcities = [
    "አዲስ ከተማ", "አቃቂ ቃሊቲ", "አራዳ", "ቦሌ", "ጉለሌ", 
    "ቂርቆስ", "ኮልፌ ቀራኒዮ", "ለሚ ኩራ", "ልደታ", "ንፋስ ስልክ ላፍቶ", "የካ"
  ];

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const isOnline = useOnlineStatus();
  const { addOfflineMember } = useOfflineSync();
  // Multiple disability types state
  const [disabilityTypes, setDisabilityTypes] = useState([""]);
  const [disabilityOthersMap, setDisabilityOthersMap] = useState({}); // {index: otherText}

  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    const currentUserEmail = currentUser?.email;

    if (editingMember) {
      setFormData({
        ...initialState,
        ...editingMember,
        
      });
      // Initialize disabilityTypes from existing data if present
      const options = [
        "ዓይነ ስውር",
        "መስማት የተሳነው",
        "የአካል / የመንቀሳቀስ እክል",
        "የአዕምሮ እድገት ውስንነት",
        "ኦቲዝም/የነርቭ ስርዓት እክል",
        "የአእምሮ ጤና እክል",
        "ሥር የሰደደ ሕመም",
        "የመናገር እክል",
        "የተለየ",
      ];
      const raw = (editingMember.disabilityType || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (raw.length === 0) {
        setDisabilityTypes([""]);
        setDisabilityOthersMap({});
      } else {
        const nextTypes = [];
        const nextOthers = {};
        raw.forEach((val, idx) => {
          if (options.includes(val)) {
            nextTypes.push(val);
          } else {
            nextTypes.push("Other");
            nextOthers[idx] = val;
          }
        });
        setDisabilityTypes(nextTypes);
        setDisabilityOthersMap(nextOthers);
      }
    } else {
      // Reset when adding new
      setDisabilityTypes([""]);
      setDisabilityOthersMap({});
    }
  }, [editingMember]);

  // Load OPD sub city for the current user and lock Sub City field
  useEffect(() => {
  const loadOPDSubCity = async () => {
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    const currentUserEmail = currentUser?.email;

    if (!currentUserEmail) return;

    // 1️⃣ Use offline fallback from localStorage
    if (!navigator.onLine) {
      const fallbackSubCity = currentUser?.subCity || "";
      const fallbackOpdName = currentUser?.opdName || "";

      setFormData(prev => ({
        ...prev,
        subCity: fallbackSubCity || prev.subCity,
        opdName: fallbackOpdName || prev.opdName
      }));

      return; // stop here (no fetch)
    }

    // 2️⃣ Online → fetch from API
    try {
      const resp = await fetch(
        `${API_BASE_URL}/get_opd_by_email.php?email=${encodeURIComponent(currentUserEmail)}`
      );
      const data = await resp.json();

      if (data?.status === "success" && data?.data) {
        const subCity = data.data.sub_city;
        const opdName = data.data.name || data.data.opd_name || "";

        // Store for offline use later
        localStorage.setItem(
          "currentUser",
          JSON.stringify({
            ...currentUser,
            subCity,
            opdName
          })
        );

        setFormData(prev => ({
          ...prev,
          subCity,
          opdName
        }));
      }
    } catch (e) {
      console.error("Failed to load OPD sub city:", e);
    }
  };

  loadOPDSubCity();
}, []);


  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "idFile") {
      setFormData({ ...formData, idFile: files && files[0] ? files[0] : null });
    } else if (name === "photoFile") {
      setFormData({ ...formData, photoFile: files && files[0] ? files[0] : null });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    if (!formData.middleName.trim()) newErrors.middleName = "Middle name is required";
    if (!formData.gender.trim()) newErrors.gender = "Gender is required";
    if (!formData.subCity.trim()) newErrors.subCity = "Sub City is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    let formDataToSend;

    try {
      // Build disabilityType string from multiple selections
      const disabilityTypeString = disabilityTypes
        .map((val, idx) => {
          if (val === "Other") {
            const other = (disabilityOthersMap[idx] || formData.disabilityOther || "").trim();
            return other || "Other";
          }
          return val;
        })
        .filter(Boolean)
        .join(", ");
      
      if (editingMember && editingMember.id) {
        // For editing, always require online connection
        if (!isOnline) {
          alert("You need to be online to edit existing members.");
          setSubmitting(false);
          return;
        }

        // Update via API
        formDataToSend = new FormData();
        
        // Add all form data to FormData
        Object.keys(formData).forEach(key => {
          if (formData[key] !== null && formData[key] !== undefined) {
            if ((key === 'idFile' || key === 'photoFile') && formData[key] instanceof File) {
              formDataToSend.append(key, formData[key]);
            } else if (key !== 'idFile' && key !== 'photoFile') {
              if (key === 'disabilityType') {
                formDataToSend.append('disabilityType', disabilityTypeString);
              } else {
                formDataToSend.append(key, formData[key]);
              }
            }
          }
        });
        
        // Add required fields for update - ensure ID is the first field added
        formDataToSend.append('id', editingMember.id.toString());  // Convert to string to ensure proper formatting
        formDataToSend.append('requestEmail', currentUserEmail);

        // Log the form data for debugging
        console.log('Updating member with ID:', editingMember.id);
        console.log('Editing member object:', editingMember);
        
        // Create a plain object version of formData for logging
        const formDataObj = {};
        formDataToSend.forEach((value, key) => {
          formDataObj[key] = value;
        });
        
        console.log('Form data to be sent (plain object):', formDataObj);
        console.log('Form data entries:');
        for (let [key, value] of formDataToSend.entries()) {
          console.log(`- ${key}:`, value);
        }
        console.log('Current user email:', currentUserEmail);

        // Send multipart form data to updateMember.php to allow file upload
        const requestUrl = `${API_BASE_URL}/updateMember.php`;
        console.log('Sending multipart request to:', requestUrl);
        const response = await fetch(requestUrl, {
          method: 'POST',
          body: formDataToSend, // No manual headers; browser sets multipart boundary
        });

        console.log('Update response status:', response.status);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || `HTTP error! status: ${response.status}`);
        }

        if (data.status === 'success') {
          alert("Member updated successfully!");
          if (onMemberAdded) onMemberAdded();
          if (onClose) onClose();
          return;
        } else {
          throw new Error(data.message || 'Failed to update member');
        }
      } else {
        // Adding new member
        if (isOnline) {
  // Create FormData for file + text fields
  const formDataToSend = new FormData();

  // Add all text fields from your form
  Object.keys(formData).forEach((key) => {
    if (formData[key] !== null && formData[key] !== undefined) {
      if ((key === "idFile" || key === "photoFile") && formData[key] instanceof File) {
        formDataToSend.append(key, formData[key]); // attach file
      } else {
        if (key === 'disabilityType') {
          formDataToSend.append('disabilityType', disabilityTypeString);
        } else {
          formDataToSend.append(key, formData[key]); // attach text
        }
      }
    }
  });

  // ✅ Add the OPD’s email and name
  formDataToSend.append("opdEmail", currentUserEmail);
  formDataToSend.append("opd_name", formData.opdName || "");

  // ✅ Send the data (no headers, because FormData sets it automatically)
  const response = await fetch(`${API_BASE_URL}/addMember.php`, {
    method: "POST",
    body: formDataToSend,
  });

  const data = await response.json();

  if (data.status === "success") {
    alert("Member added successfully");
    setFormData(initialState);
    setDisabilityTypes([""]);
    setDisabilityOthersMap({});
    setErrors({});
    if (onMemberAdded) onMemberAdded();
    if (onClose) onClose();
  } else {
    alert("Failed to add member: " + data.message);
  }
} else {
  // Offline save
  addOfflineMember({ ...formData, disabilityType: disabilityTypeString, opdEmail: currentUserEmail, opd_name: formData.opdName || "" });
  alert("Member saved offline - Will sync when reconnected");
  // Reset form and close modal after offline save
  setFormData(initialState);
  setDisabilityTypes([""]);
  setDisabilityOthersMap({});
  setErrors({});
  if (onMemberAdded) onMemberAdded();
  if (onClose) onClose();
}

      }
    } catch (error) {
      console.error("Failed to save member:", error);
      let errorMessage = error.message || "Failed to save member. Please check your connection.";
      
      // More specific error messages
      if (error.message.includes('Failed to fetch')) {
        errorMessage = "Unable to connect to the server. Please check your internet connection.";
      } else if (error.message.includes('Invalid JSON')) {
        errorMessage = "Received an invalid response from the server. Please try again.";
      }
      
      console.error("Detailed error:", {
        message: error.message,
        name: error.name,
        stack: error.stack,
        formData: Object.fromEntries(formDataToSend.entries())
      });
      
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const renderInput = (name, label, type = "text", required = false) => (
    <div key={name}>
      <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={formData[name] || ""}
        onChange={handleChange}
        required={required}
        readOnly={name === "location" || name === "subCity"} // lock Location and Sub City
        className={`w-full border-gray-300 rounded-lg shadow-sm focus:ring-teal-500 focus:border-teal-500 transition ${errors[name] ? "border-red-500 ring-red-500" : "border-gray-300"}`}
      />
      {errors[name] && <span className="text-red-600 text-xs mt-1">{errors[name]}</span>}
    </div>
  );

  const renderSelect = (name, label, options = [], required = false) => (
    <div key={name}>
      <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        id={name}
        name={name}
        value={formData[name] || ""}
        onChange={handleChange}
        required={required}
        className={`w-full border-gray-300 rounded-lg shadow-sm focus:ring-teal-500 focus:border-teal-500 transition ${errors[name] ? "border-red-500 ring-red-500" : "border-gray-300"}`}
      >
        <option value="">Select {label}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
      {errors[name] && <span className="text-red-600 text-xs mt-1">{errors[name]}</span>}
    </div>
  );

  // Handlers for dynamic disability types
  const handleDisabilityTypeChange = (index, value) => {
    const next = [...disabilityTypes];
    next[index] = value;
    setDisabilityTypes(next);
  };

  const handleDisabilityOtherChange = (index, value) => {
    setDisabilityOthersMap((prev) => ({ ...prev, [index]: value }));
  };

  const addDisabilityTypeField = () => {
    setDisabilityTypes((prev) => [...prev, ""]);
  };

  const removeDisabilityTypeField = (index) => {
    setDisabilityTypes((prev) => prev.filter((_, i) => i !== index));
    setDisabilityOthersMap((prev) => {
      const { [index]: _removed, ...rest } = prev;
      // reindex is not necessary as we key by index at render time
      return rest;
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* OPD Info */}
      <fieldset className="border border-gray-200 rounded-lg p-4">
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">OPD Email</label>
            <input
              type="email"
              value={currentUserEmail}
              readOnly
              className="w-full border-gray-300 rounded-lg bg-gray-100 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">OPD Name</label>
            <input
              type="text"
              value={formData.opdName || ""}
              readOnly
              className="w-full border-gray-300 rounded-lg bg-gray-100 shadow-sm"
            />
          </div>
        </div>
      </fieldset>

      {/* Personal Info */}
      <fieldset className="border border-gray-200 rounded-lg p-4">
        <legend className="font-semibold text-gray-800 px-2 text-lg">የግል መረጃ</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderInput("firstName", "ስም", "text", true)}
          {renderInput("middleName", "የአባት ስም", "text", true)}
          {renderInput("lastName", "የአያት ስም", "text")}
          {renderSelect("gender", "ፆታ", ["ወንድ", "ሴት"], true)}
          {renderInput("dateOfBirth", "የትውልድ ቀን", "date")}
          {renderInput("age", "እድሜ", "number")}
          {renderInput("phoneNumber", "ስልክ ቁጥር", "tel")}
          {renderInput("AltPhoneNumber", "ተለዋጭ ስልክ ቁጥር", "tel")}
          {renderInput("email", "ኢሜል", "email")}
        </div>
      </fieldset>

      {/* Address */}
      <fieldset className="border border-gray-200 rounded-lg p-4">
        <legend className="font-semibold text-gray-800 px-2 text-lg">አድራሻ</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderInput("location", "ከተማ")}
          {renderInput("subCity", "ክፍለ ከተማ", "text", true)}
          {renderInput("woreda", "ወረዳ")}
          {renderInput("houseNumber", "የቤት ቁጥር")}
          {renderInput("specificLocation", "ልዩ አድራሻ")}
        </div>
      </fieldset>

      {/* Disability Info */}
      <fieldset className="border border-gray-200 rounded-lg p-4">
        <legend className="font-semibold text-gray-800 px-2 text-lg">የአካል ጉዳት መረጃ</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {disabilityTypes.map((val, idx) => (
            <div key={idx} className="col-span-1 md:col-span-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    የአካል ጉዳት መረጃ {idx + 1}
                  </label>
                  <select
                    value={val}
                    onChange={(e) => handleDisabilityTypeChange(idx, e.target.value)}
                    className={`w-full border-gray-300 rounded-lg shadow-sm focus:ring-teal-500 focus:border-teal-500 transition`}
                  >
                    <option value="">የአካል ጉዳት ዓይነት ይምረጡ</option>
                    {[
                      "ዓይነ ስውር",
                      "መስማት የተሳነው",
                      "የአካል / የመንቀሳቀስ እክል",
                      "የአዕምሮ እድገት ውስንነት",
                      "የአእምሮ ጤና እክል",
                      "ሥር የሰደደ ሕመም",
                      "የመናገር እክል",
                      "ኦቲዝም/የነርቭ ስርዓት እክል",
                      "የተለየ",
                    ].map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                {val === "Other" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      እባክዎ የአካል ጉዳት ዓይነቱን ይግለጹ
                    </label>
                    <input
                      type="text"
                      value={disabilityOthersMap[idx] || formData.disabilityOther || ""}
                      onChange={(e) => handleDisabilityOtherChange(idx, e.target.value)}
                      className={`w-full border-gray-300 rounded-lg shadow-sm focus:ring-teal-500 focus:border-teal-500 transition`}
                    />
                  </div>
                )}
                {disabilityTypes.length > 1 && (
                  <div className="flex md:justify-start">
                    <button
                      type="button"
                      onClick={() => removeDisabilityTypeField(idx)}
                      className="px-4 py-2 mt-6 rounded-lg bg-red-100 text-red-700 hover:bg-red-200"
                    >
                      ሰርዝ
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}

          <div className="col-span-1 md:col-span-2">
            <button
              type="button"
              onClick={addDisabilityTypeField}
              className="px-4 py-2 rounded-lg bg-teal-50 text-teal-700 hover:bg-teal-100"
            >
              የአካል ጉዳት ዓይነት ጨምር
            </button>
          </div>

          {renderInput("disabilityDescription", "የአካል ጉዳት አይነት በዝርዝር")}
        </div>
      </fieldset>

      {/* Support Info */}
      <fieldset className="border border-gray-200 rounded-lg p-4">
        <legend className="font-semibold text-gray-800 px-2 text-lg">የድጋፍ መረጃ</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderInput("supportNeeded", "የሚያስፈልግ ድጋፍ")}
          {renderInput("workNeeded", "የሚፈለገው የሥራ ዓይነት")}
          {renderInput("supportReceived", "የተቀበሉት ድጋፍ")}
        </div>
      </fieldset>

      {/* Other Info */}
      <fieldset className="border border-gray-200 rounded-lg p-4">
        <legend className="font-semibold text-gray-800 px-2 text-lg">ተጨማሪ መረጃ</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderSelect("maritalStatus", "የጋብቻ ሁኔታ", ["ያላገባ/ያላገባች", "ያገባ/ያገባች", "የፈታ/የፈታች", "መበለት"])}
          {renderInput("numberOfFamilies", "የቤተሰብ ብዛት")}
          {renderSelect(
            "educationLevel",
            "የትምህርት ደረጃ",
            [
              "መደበኛ ትምህርት ያሎሰደ",
              "አንደኛ ደረጃ",
              "ሁለተኛ ደረጃ",
              "ዲፕሎማ",
              "የመጀመሪያ ዲግሪ",
              "ማስተርስ",
              "ዶክትሬት",
              "ሌላ",
            ]
          )}
          {renderSelect("employmentStatus", "የሥራ ሁኔታ", ["ተቀጣሪ", "ሥራ አጥ", "ተማሪ", "ጡረተኛ", "ሌላ"])}
          {formData.employmentStatus === "ተቀጣሪ" && (
            <>
              {renderInput("workPlace", "የሥራ ቦታ")}
              {renderInput("officeName", "የመስሪያ ቤቱ ስም")}
              {renderInput("workType", "የሥራው ዓይነት")}
              {renderInput("jobResponsibility", "የሥራ ኃላፊነት")}
            </>
          )}
          {renderSelect("labourID", "የሠራተኛ መለያ ቁጥር (Labour ID)", ["ያለው", "የሌለው"])}
          {formData.labourID === "ያለው" && renderInput("labourIDNumber", "የሠራተኛ መለያ ቁጥር (Labour ID Number)")}
          {renderSelect("idType", "የመታወቂያ ዓይነት", ["የነዋሪነት መታወቂያ", "የመስሪያ ቤት የመታወቂያ", "ፓስፖርት", "ብሔራዊ መታወቂያ", "የስደተኛ መታወቂያ", "ሌላ"])}
          {renderInput("idNumber", "የመታወቂያ ቁጥር", "text")}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ደረሰኝ</label>
            <input
              type="file"
              name="idFile"
              onChange={handleChange}
              accept="image/*,.pdf,.doc,.docx"
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
            />
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">የፓስፖርት መጠን ፎቶ</label>
              <input
                type="file"
                name="photoFile"
                onChange={handleChange}
                accept="image/*"
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
              />
            </div>
            {formData.photoAttachment && typeof formData.photoAttachment === 'string' && (
              <div className="mt-2">
                {isImageFile(formData.photoAttachment) ? (
                  <img
                    src={`${ATTACH_BASE_URL}/${formData.photoAttachment}`}
                    alt="Current Passport Photo"
                    className="max-h-48 rounded-lg border border-gray-200 shadow-sm"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <a
                    href={`${ATTACH_BASE_URL}/${formData.photoAttachment}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:underline"
                  >
                    View current passport photo
                  </a>
                )}
              </div>
            )}
            {formData.idAttachment && typeof formData.idAttachment === 'string' && (
              <div className="mt-2">
                {isImageFile(formData.idAttachment) ? (
                  <img
                    src={`${ATTACH_BASE_URL}/${formData.idAttachment}`}
                    alt="Current Receipt"
                    className="max-h-48 rounded-lg border border-gray-200 shadow-sm"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <a
                    href={`${ATTACH_BASE_URL}/${formData.idAttachment}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:underline"
                  >
                    View current attachment
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </fieldset>

      <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onClose}
          className="px-6 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className={`px-6 py-2 rounded-lg text-white font-semibold transition ${submitting ? "bg-teal-400 cursor-not-allowed" : "bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"}`}
        >
          {submitting ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              {isOnline ? 'Saving...' : 'Saving Offline...'}
            </span>
          ) : (
            <>
              {editingMember ? "Update Member" : "Add Member"}
              {!isOnline && !editingMember && (
                <span className="ml-2 text-xs opacity-75">(Offline)</span>
              )}
            </>
          )}
        </button>
      </div>
    </form>
  );
}